// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import type { BlockingActionResourceData } from 'app/packages/pinterest-flow-types/board';
import withShowToast from 'app/common/lib/react/withShowToast';
import { compose } from 'redux';
import { fetchBlockingActions } from 'app/common/redux/actions/blockingActions';
import BoardBlockingActionToast, {
  type BlockingActionType,
} from 'app/common/react/components/save/board/BoardBlockingActionToast/BoardBlockingActionToast';

const IN_PROGRESS_TOAST_DURATION = 2000;
const COMPLETED_TOAST_DURATION = 2000;
const LONG_POLL_INTERVAL = 5000;

function eventTypeToBlockingActionType(eventType: number): ?BlockingActionType {
  switch (eventType) {
    case PCONST.thrift.EventType.BOARD_DELETE:
      return 'delete_board';
    case PCONST.thrift.EventType.BOARD_MERGE:
    case PCONST.thrift.EventType.BOARD_SECTION_MERGE:
      return 'merge';
    case PCONST.thrift.EventType.BULK_PIN_DELETE:
    case PCONST.thrift.EventType.BOARD_SECTION_REMOVE_PINS:
    case PCONST.thrift.EventType.BULK_SELECT_ALL_PIN_DELETE:
      return 'delete_pins';
    case PCONST.thrift.EventType.BULK_PIN_MOVE:
    case PCONST.thrift.EventType.BULK_SELECT_ALL_PIN_MOVE:
      return 'move_pins';
    case PCONST.thrift.EventType.BOARD_SECTION_CREATE:
      return 'create_section';
    case PCONST.thrift.EventType.BOARD_SECTION_DELETE:
      return 'delete_section';
    default:
      return null;
  }
}

const MOVE_ACTIONS = ['merge', 'move_pins', 'create_section'];

function findMatchingBlockingActionInResponse({
  blockingActions,
  actionType,
  boardId,
  sectionId,
}: {
  blockingActions: Array<BlockingActionResourceData>,
  actionType: BlockingActionType,
  boardId: string,
  sectionId: ?string,
}) {
  return blockingActions.find(
    (action) =>
      eventTypeToBlockingActionType(action.action_type) === actionType &&
      !(MOVE_ACTIONS.includes(action.action_type) && action.is_origin) &&
      action.board_id === boardId &&
      (sectionId
        ? sectionId === action.section_id
        : !action.section_id || action.section_id === '-1') &&
      action.progress !== 1,
  );
}

type Props = {
  dispatch: Function,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  showToast: (React.ComponentType<*>) => void,
};

let nextId = 0;
const timeoutMap: { [id: string]: TimeoutID } = {};

function addCompletionListener({
  check,
  checkDelay,
  checkInterval,
  onComplete,
}: {
  check: () => Promise<boolean>,
  checkDelay: number,
  checkInterval: number,
  onComplete: () => void,
}) {
  const id = `timeout${nextId}`;
  nextId += 1;
  const longPoll = () => {
    const timeBeforeCheck = Date.now();
    check()
      .then((completed) => {
        const checkTimeSpent = Date.now() - timeBeforeCheck;
        if (completed) {
          onComplete();
          delete timeoutMap[id];
        } else {
          timeoutMap[id] = setTimeout(longPoll, Math.max(1000, checkInterval - checkTimeSpent));
        }
      })
      .catch(() => {
        delete timeoutMap[id];
      });
  };
  timeoutMap[id] = setTimeout(longPoll, checkDelay);
}

export default function withShowBlockingActionToasts(
  Subject: React.ComponentType<any>,
): React.ComponentType<any> {
  class BlockingActionToaster extends React.Component<Props> {
    // $FlowFixMe: new errors found from flow 0.96 upgrade
    static displayName = `WithShowBlockingActionToasts(${Subject.displayName || Subject.name})`;
    showBlockingActionToast = ({
      actionType,
      board,
      section,
    }: {
      actionType: BlockingActionType,
      board: { id: string, name: string },
      section?: null | { id: string, title: string },
    }) => {
      const { showToast } = this.props;
      const getToast = (actionStatus: 'inProgress' | 'completed') => ({ onHide }) => (
        <BoardBlockingActionToast
          actionType={actionType}
          actionStatus={actionStatus}
          duration={
            actionStatus === 'inProgress' ? IN_PROGRESS_TOAST_DURATION : COMPLETED_TOAST_DURATION
          }
          location={{ name: section ? section.title : board.name, isSection: !!section }}
          onHide={onHide}
        />
      );
      showToast(getToast('inProgress'));
      addCompletionListener({
        check: () => this.poll(actionType, board.id, section && section.id),
        checkDelay: IN_PROGRESS_TOAST_DURATION,
        checkInterval: LONG_POLL_INTERVAL,
        onComplete: () => showToast(getToast('completed')),
      });
    };

    poll = (actionType: BlockingActionType, boardId: string, sectionId: ?string) =>
      this.props.dispatch(fetchBlockingActions(boardId)).then(
        (blockingActions) =>
          !findMatchingBlockingActionInResponse({
            blockingActions,
            actionType,
            boardId,
            sectionId,
          }),
      );

    render() {
      return <Subject {...this.props} showBlockingActionToast={this.showBlockingActionToast} />;
    }
  }

  return compose(
    // $FlowFixMe Flow v0.104.0 upgrade supression.
    connect<*, *, _, _, _, _>(),
    withShowToast,
  )(BlockingActionToaster);
}
